/**
 * Main loop: fixed-rate updates for UI animations, render every display frame, screen
 * transitions with a short fade through black, and the D2 cursor drawn last.
 */
import type { Assets } from '../assets/store';
import type { AudioEngine } from '../audio/audio';
import { type Sprite } from '../gfx/atlas';
import { type Renderer, VIEW_H, VIEW_W } from '../gfx/renderer';
import { errorText, report } from '../net/report';
import { Input, type InputSink } from './input';
import type { CursorKind, Screen } from './screen';

export class App {
  readonly input: Input;
  screen: Screen | null = null;
  private pending: Screen | null = null;
  private fade = 1; // 1 = black
  private fadeDir: -1 | 1 = -1;
  private last = performance.now();
  time = 0;
  private cursorSprites: Partial<Record<CursorKind, Sprite[]>> = {};
  /** Extra overlay drawn above the screen (e.g. modal dialogs). */
  overlays: { render(r: Renderer): void; sink?: InputSink }[] = [];
  /** Drawn above everything but the cursor, fade included (see `ui/loading-curtain`). */
  curtain: ((r: Renderer, dt: number) => void) | null = null;
  fps = 0;
  private frames = 0;
  private fpsT = 0;

  constructor(
    readonly r: Renderer,
    readonly assets: Assets,
    readonly audio: AudioEngine,
  ) {
    this.input = new Input(r, r.canvas);
    const forward: InputSink = {
      onMouseDown: (x, y, b, e) => this.target()?.onMouseDown?.(x, y, b, e),
      onMouseUp: (x, y, b, e) => this.target()?.onMouseUp?.(x, y, b, e),
      onMouseMove: (x, y, e) => this.target()?.onMouseMove?.(x, y, e),
      onWheel: (d, e) => this.target()?.onWheel?.(d, e),
      onKeyDown: (e) => this.target()?.onKeyDown?.(e),
      onKeyUp: (e) => this.target()?.onKeyUp?.(e),
      onText: (t) => this.target()?.onText?.(t),
    };
    this.input.sink = forward;
    window.addEventListener('resize', () => r.resize());
    r.resize();
  }

  private target(): InputSink | null {
    if (this.fadeDir === 1 && this.pending) return null;
    const top = this.overlays[this.overlays.length - 1];
    return top?.sink ?? this.screen;
  }

  async loadCursors(): Promise<void> {
    const base = 'data\\global\\ui\\CURSOR\\';
    this.cursorSprites.hand = await this.assets.dc6(`${base}ohand.DC6`);
    this.cursorSprites.press = await this.assets.dc6(`${base}lpress.DC6`);
    this.cursorSprites.grasp = await this.assets.dc6(`${base}Grasp.DC6`);
    // buysell.dc6 holds one cel per cursor mode (not an animation): the open hand buys, the
    // hand with the coin sells, the magnifying glass identifies.
    const modes = await this.assets.dc6(`${base}buysell.DC6`);
    this.cursorSprites.buy = modes[3] ? [modes[3]] : [];
    this.cursorSprites.sell = modes[4] ? [modes[4]] : [];
    this.cursorSprites.identify = modes[8] ? [modes[8]] : [];
  }

  /** Switches screens: fade out, load the next, fade in. */
  async go(next: Screen): Promise<void> {
    next.app = this;
    this.pending = next;
    this.fadeDir = 1;
    const loading = next.load();
    await new Promise<void>((res) => {
      const check = () => (this.fade >= 1 ? res() : requestAnimationFrame(check));
      check();
    });
    await loading;
    if (this.pending !== next) return;
    this.screen?.leave();
    this.screen = next;
    this.pending = null;
    next.enter();
    this.fadeDir = -1;
  }

  start(): void {
    const loop = (t: number) => {
      const dt = Math.min(0.1, (t - this.last) / 1000);
      this.last = t;
      this.time += dt;
      this.frame(dt);
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }

  private frame(dt: number): void {
    this.fade = Math.max(0, Math.min(1, this.fade + this.fadeDir * dt * 5));
    // An exception here used to end the animation loop for good (a frozen screen with no
    // message). It is reported once per kind and the loop carries on.
    this.guard('update', () => this.screen?.update(dt));
    const r = this.r;
    r.resize();
    r.begin();
    this.guard('render', () => {
      this.screen?.render(r);
      for (const o of this.overlays) o.render(r);
    });
    if (this.fade > 0) r.fillRect(0, 0, VIEW_W, VIEW_H, [0, 0, 0, this.fade]);
    // Drawn over the fade: the "Loading..." door while a game loads between screens.
    if (this.curtain) this.guard('curtain', () => this.curtain?.(r, dt));
    this.drawCursor();
    r.end();
    this.frames++;
    this.fpsT += dt;
    if (this.fpsT >= 1) {
      this.fps = this.frames / this.fpsT;
      this.frames = 0;
      this.fpsT = 0;
    }
  }

  private readonly reported = new Set<string>();

  private guard(what: string, fn: () => void): void {
    try {
      fn();
    } catch (e) {
      const text = errorText(e);
      const key = `${what} ${text.slice(0, 200)}`;
      if (this.reported.has(key)) return;
      this.reported.add(key);
      report(`${this.screen?.constructor.name ?? 'screen'} ${what} failed`, text);
    }
  }

  private drawCursor(): void {
    const kind = this.screen?.cursor() ?? 'hand';
    if (kind === 'none') return;
    const down = this.input.buttons.has(0) || this.input.buttons.has(2);
    const set = (kind === 'hand' && down ? this.cursorSprites.press : this.cursorSprites[kind]) ?? this.cursorSprites.hand;
    if (!set?.length) return;
    // Pressed cursor animates once; idle hand is a single frame.
    const s = set[Math.min(set.length - 1, Math.floor(this.time * 25) % set.length)] ?? set[0];
    this.r.draw(set === this.cursorSprites.press ? set[0] : s, Math.round(this.input.x), Math.round(this.input.y) - 1, { topLeft: true });
  }
}
